import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import styles from '../styles';
import { getFabrics, clearFabrics } from '../Actions';
import { errorNotification, successNotification } from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';

const FabricList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const fabrics = useSelector(state => state.fabrics);

  useEffect(() => {
    dispatch(getFabrics());
    return () => {
      dispatch(clearFabrics());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/fabrics/${id}`);
      successNotification('Fabric deleted');
      dispatch(getFabrics());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateFabric) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/fabrics/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteFabric) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Fabric',
        dataIndex: 'fabric',
        sorter: sorter('fabric'),
        onFilter: onFilter('fabric'),
        filterDropdown,
        filterIcon,
        width: '15%',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: sorter('name'),
        onFilter: onFilter('name'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quality',
        dataIndex: 'quality',
        sorter: sorter('quality'),
        onFilter: onFilter('quality'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Count',
        dataIndex: 'count',
        sorter: sorter('count'),
        onFilter: onFilter('count'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Read',
        dataIndex: 'constructionRead',
        sorter: sorter('constructionRead'),
        onFilter: onFilter('constructionRead'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Pick',
        dataIndex: 'constructionPick',
        sorter: sorter('constructionPick'),
        onFilter: onFilter('constructionPick'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Width',
        dataIndex: 'width',
        sorter: sorter('width'),
        onFilter: onFilter('width'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        sorter: sorter('unit'),
        onFilter: onFilter('unit'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Composition',
        dataIndex: 'composition',
        sorter: sorter('composition'),
        onFilter: onFilter('composition'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateFabric)) {
      column.push({
        title: 'Update',
        width: '7%',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteFabric)) {
      column.push({
        title: 'Delete',
        width: '6%',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1600, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Fabric"
        moduleLink="/fabrics/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={fabrics}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

FabricList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(FabricList);
